import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { PremiumFeature } from './PremiumFeature';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useTheme } from '../contexts/ThemeContext';

interface SidebarProps {
  user: {
    name: string;
    email: string;
    role: string;
    avatar_url?: string;
    is_premium: boolean;
  };
  collapsed?: boolean;
}

const navItems = [
  { label: 'Home', icon: <HomeIcon />, path: '/home', premium: false },   
  { label: 'Goals', icon: <DashboardIcon />, path: '/goals', premium: false },
  { label: 'Tasks', icon: <AssignmentIcon />, path: '/tasks', premium: false },
  { label: 'Forum', icon: <DashboardIcon />, path: '/forum', premium: false },
  { label: 'Games', icon: <SportsEsportsIcon />, path: '/games', premium: true },
];

const SIDEBAR_WIDTH = 282;
const SIDEBAR_MINI_WIDTH = 40;

const Sidebar: React.FC<SidebarProps> = ({ user, collapsed }) => {
  const [open, setOpen] = useState(collapsed === undefined ? true : !collapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, toggleTheme } = useTheme();

  // Update open state when collapsed prop changes
  useEffect(() => {
    setOpen(collapsed === undefined ? true : !collapsed);
  }, [collapsed]);

  // Close mobile sidebar when route changes
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleLogout = async () => {
    await import('../lib/supabase').then(({ supabase }) => supabase.auth.signOut());
    navigate('/login');
  };

  const sidebarContent = (
    <Box
      sx={{
        width: open ? SIDEBAR_WIDTH : SIDEBAR_MINI_WIDTH,
        minWidth: open ? SIDEBAR_WIDTH : SIDEBAR_MINI_WIDTH,
        transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
        bgcolor: 'background.paper',
        color: 'text.primary',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 1,
        borderTopRightRadius: { xs: 0, md: 20 },
        borderBottomRightRadius: { xs: 0, md: 20 },
        py: 2,
        px: open ? 2 : 0.5,
        overflow: 'hidden',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Toggle Button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 2, 
        mt: 1, 
        justifyContent: open ? 'space-between' : 'center',
        px: { xs: 1, md: 0 }
      }}>
        {(!isMobile || (isMobile && mobileOpen)) && (
          <>
            <IconButton
              onClick={toggleTheme}
              sx={{ 
                color: 'text.primary', 
                bgcolor: 'action.hover', 
                '&:hover': { bgcolor: 'action.selected' },
                display: { xs: 'none', md: 'flex' }
              }}
              size="small"
            >
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <IconButton
              onClick={() => setOpen((prev) => !prev)}
              sx={{ 
                color: 'text.primary', 
                bgcolor: 'action.hover', 
                '&:hover': { bgcolor: 'action.selected' }, 
                mb: open ? 0 : 1,
                display: { xs: 'none', md: 'flex' }
              }}
              size="small"
            >
              {open ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
          </>
        )}
      </Box>

      {/* Profile Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: open ? 2 : 0,
          mb: 2,
          flexDirection: open ? 'row' : 'column',
          justifyContent: 'center',
          cursor: 'pointer',
          px: { xs: 1, md: 0 }
        }}
        onClick={() => {
          if (isMobile) setMobileOpen(false);
          navigate('/profile');
        }}
      >
        <Avatar 
          src={user.avatar_url && user.avatar_url.length > 0 ? user.avatar_url : undefined}
          sx={{ 
            width: { xs: 40, md: open ? 56 : 40 }, 
            height: { xs: 40, md: open ? 56 : 40 }, 
            bgcolor: 'primary.main', 
            fontWeight: 700, 
            fontSize: { xs: 20, md: open ? 32 : 20 } 
          }}
        >
          {(!user.avatar_url || user.avatar_url.length === 0) && (user.name && user.name.length > 0 ? user.name[0] : '?')}
        </Avatar>
        {open && (
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.1} noWrap color="text.primary">
              {user.name && user.name.length > 0 ? user.name : 'No Name'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }} noWrap color="text.secondary">
              {user.email && user.email.length > 0 ? user.email : 'No Email'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.role && user.role.length > 0 ? user.role : 'User'}
            </Typography>
          </Box>
        )}
      </Box>
      <Divider sx={{ bgcolor: 'divider', mb: 2 }} />
      {/* Navigation */}
      <List sx={{ flexGrow: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding sx={{ mb: 1 }}>
            <PremiumFeature
              featureName={item.label}
              disabled={item.premium && !user.is_premium}
            >
              <ListItemButton
                onClick={(e) => {
                  e.preventDefault();
                  if (isMobile) setMobileOpen(false);
                  if (!item.premium || user.is_premium) {
                    navigate(item.path, { replace: true });
                  }
                }}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 2,
                  bgcolor: location.pathname === item.path ? 'primary.main' : 'transparent',
                  color: location.pathname === item.path ? 'primary.contrastText' : 'text.primary',
                  '&:hover': {
                    bgcolor: location.pathname === item.path ? 'primary.dark' : 'action.hover',
                  },
                  fontWeight: 600,
                  minHeight: 48,
                  justifyContent: open ? 'flex-start' : 'center',
                  px: open ? 2 : 1,
                  textDecoration: 'none',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? 'primary.contrastText' : 'text.primary',
                    minWidth: 0,
                    mr: open ? 2 : 'auto',
                    justifyContent: 'center',
                    transition: 'color 0.2s',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ 
                      fontWeight: 600, 
                      fontSize: 18,
                      color: 'inherit',
                    }}
                  />
                )}
              </ListItemButton>
            </PremiumFeature>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ bgcolor: 'divider', my: 2 }} />
      {/* Settings at the bottom */}
      <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', gap: 1, justifyContent: open ? 'flex-start' : 'center' }}>
        <ListItemButton
          onClick={(e) => {
            e.preventDefault();
            if (isMobile) setMobileOpen(false);
            navigate('/settings', { replace: true });
          }}
          selected={location.pathname === '/settings'}
          sx={{
            borderRadius: 2,
            color: location.pathname === '/settings' ? 'primary.contrastText' : 'text.primary',
            bgcolor: location.pathname === '/settings' ? 'primary.main' : 'transparent',
            '&:hover': { 
              bgcolor: location.pathname === '/settings' ? 'primary.dark' : 'action.hover' 
            },
            minHeight: 48,
            justifyContent: open ? 'flex-start' : 'center',
            px: open ? 2 : 1,
            textDecoration: 'none',
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '& .MuiListItemIcon-root': {
                color: 'primary.contrastText',
              },
            },
          }}
        >
          <ListItemIcon 
            sx={{ 
              color: location.pathname === '/settings' ? 'primary.contrastText' : 'text.primary',
              minWidth: 0, 
              mr: open ? 2 : 'auto', 
              justifyContent: 'center',
              transition: 'color 0.2s',
            }}
          >
            <SettingsIcon />
          </ListItemIcon>
          {open && (
            <ListItemText 
              primary="Settings" 
              primaryTypographyProps={{ 
                fontWeight: 600, 
                fontSize: 18,
                color: 'inherit',
              }} 
            />
          )}
        </ListItemButton>
        <Divider sx={{ bgcolor: 'divider', my: 1 }} />
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: 'text.primary',
            '&:hover': { bgcolor: 'action.hover' },
            minHeight: 48,
            justifyContent: open ? 'flex-start' : 'center',
            px: open ? 2 : 1,
          }}
        >
          <ListItemIcon sx={{ color: 'text.primary', minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center' }}>
            <LogoutIcon />
          </ListItemIcon>
          {open && (
            <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600, fontSize: 18 }} />
          )}
        </ListItemButton>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{ 
            position: 'fixed', 
            top: 16, 
            left: 16, 
            zIndex: 1301, 
            color: 'primary.main', 
            bgcolor: 'background.paper', 
            boxShadow: 2,
            display: { xs: 'flex', md: 'none' }
          }}
        >
          <MenuIcon />
        </IconButton>
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ 
            sx: { 
              width: SIDEBAR_WIDTH, 
              bgcolor: 'background.paper', 
              color: 'text.primary',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0
            } 
          }}
        >
          {sidebarContent}
        </Drawer>
      </>
    );
  }

  return sidebarContent;
};

export default Sidebar; 